import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [navigationConfig, setNavigationConfig] = useState(null);
  const navigate = useNavigate();

  const fetchNavigationConfig = async (accessToken) => {
    try {
      const response = await apiService.get(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/navigation-config`
      );

      if (response.ok) {
        const navConfig = await response.json();
        setNavigationConfig(navConfig);
        return navConfig;
      } else {
        console.error('Failed to fetch navigation config');
        return null;
      }
    } catch (error) {
      // Error handling is done by apiService (will show toast and redirect if auth error)
      console.error('Error fetching navigation config:', error);
      return null;
    }
  };

  const login = (userData) => {
    setUser({
      id: userData._id,
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      email: userData.email || '',
      isManager: userData.departmentInfo?.isManager || !userData.managerId,
      permissions: userData.permissions?.permissionsList || [],
      accessToken: userData.accessToken
    });

    // Fetch navigation config after login
    if (userData.accessToken) {
      fetchNavigationConfig(userData.accessToken);
    }
  };

  useEffect(() => {
    // Check if user is already logged in
    const accessToken = localStorage.getItem('accessToken');
    const userId = localStorage.getItem('userId');
    const userFirstName = localStorage.getItem('userFirstName');
    const userLastName = localStorage.getItem('userLastName');
    const userEmail = localStorage.getItem('userEmail');
    const isCollectionsManager = localStorage.getItem('isCollectionsManager') === 'true';
    const userPermissions = localStorage.getItem('userPermissions');

    if (accessToken && userId) {
      // User is authenticated
      setUser({
        id: userId,
        firstName: userFirstName || '',
        lastName: userLastName || '',
        email: userEmail || '',
        isManager: isCollectionsManager,
        permissions: userPermissions ? userPermissions.split(',') : [],
        accessToken
      });

      // Fetch navigation config for already authenticated user
      fetchNavigationConfig(accessToken);
    }

    setLoading(false);
  }, []);

  const logout = () => {
    // Clear all localStorage items
    localStorage.removeItem('accessToken');
    localStorage.removeItem('chatUserToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('userPermissions');
    localStorage.removeItem('countryCodes');
    localStorage.removeItem('shardId');
    localStorage.removeItem('userFirstName');
    localStorage.removeItem('userLastName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userDepartment');
    localStorage.removeItem('isCollectionsManager');

    setUser(null);
    setNavigationConfig(null);
    navigate('/login');
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isManager: user?.isManager || false,
    navigationConfig,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
